import React, {useContext, useEffect, useRef, useState} from 'react';
import {Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography} from '@mui/material';
import {AppContext} from '../../context/app-context';
import type {Account} from '../../types/Account';
import request from '../../util/SuperagentUtils';
import {getErrorMessage} from '../../util/NetworkErrorUtil';
import {publishVerification} from '../../services/session/verification-notifications';

export default function AccountEmailChange({account}: {account: Account}) {
  const {refreshVerification} = useContext(AppContext);
  const [action, setAction] = useState<'change' | 'cancel' | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [refreshNeeded, setRefreshNeeded] = useState(false);
  const requestContext = useRef({active: true});

  useEffect(() => {
    const current = {active: true};
    requestContext.current = current;
    setAction(null);
    setPassword('');
    setEmail('');
    setMessage('');
    setBusy(false);
    setRefreshNeeded(false);
    return () => { current.active = false; };
  }, [account.id, account.loggedInUsername]);

  const refresh = async () => {
    const current = requestContext.current;
    try {
      await refreshVerification();
      if (current.active) setRefreshNeeded(false);
    } catch {
      if (current.active) setRefreshNeeded(true);
    }
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (busy || !action) return;
    const targetEmail = action === 'cancel' ? account.pendingEmail : email.trim();
    if (!targetEmail) {
      setPassword('');
      setMessage(action === 'cancel' ? 'There is no pending email change to cancel.' : 'Enter a replacement email address.');
      return;
    }
    const current = requestContext.current;
    setBusy(true);
    setMessage('');
    try {
      const operation = action === 'cancel' ? request.delete('/api/account/email-change') : request.post('/api/account/email-change');
      await operation.send({email: targetEmail, password})
        .timeout({response: 10000, deadline: 15000});
      publishVerification();
      if (!current.active) return;
      setAction(null);
      setMessage(action === 'cancel' ? 'Email change cancelled.' : 'Email change requested. Check the replacement address for a confirmation link.');
      await refresh();
    } catch (error) {
      if (!current.active) return;
      setMessage(getErrorMessage(error, 'We could not confirm the result. Check your account status before retrying.'));
      await refresh();
    } finally {
      if (current.active) {
        setPassword('');
        setBusy(false);
      }
    }
  };

  const close = () => { if (!busy) { setAction(null); setPassword(''); setMessage(''); setEmail(''); } };
  return <Box>
    {account.pendingEmail && <Typography sx={{mb: 1}}>Verification pending for {account.pendingEmail}. Your existing address stays in use until confirmation.</Typography>}
    <Button type="button" onClick={() => { setAction('change'); setMessage(''); }}>Change company email</Button>
    {account.pendingEmail && <Button type="button" onClick={() => { setAction('cancel'); setMessage(''); }}>Cancel pending change</Button>}
    {!action && message && <Alert severity="info" role="status">{message}</Alert>}
    {refreshNeeded && <Alert severity="info">Account status is temporarily unavailable. <Button type="button" onClick={refresh}>Retry account check</Button></Alert>}
    <Dialog open={!!action} onClose={close} aria-labelledby="email-change-title">
      <Box component="form" onSubmit={submit}>
        <DialogTitle id="email-change-title">{action === 'cancel' ? 'Cancel email change' : 'Change company email'}</DialogTitle>
        <DialogContent>
          <Typography sx={{mb: 2}}>Confirm with the current password for your signed-in user. This changes the company contact email; your sign-in username stays the same.</Typography>
          {action === 'change' && <TextField label="Replacement email" type="email" value={email} onChange={event => setEmail(event.target.value)} required fullWidth margin="dense" inputProps={{maxLength: 254}}/>}
          {action === 'cancel' && <Typography>Cancel verification of {account.pendingEmail} and keep {account.email}.</Typography>}
          <TextField label="Current password" type="password" autoComplete="current-password" value={password} onChange={event => setPassword(event.target.value)} required fullWidth margin="dense" inputProps={{maxLength: 1024}}/>
          {message && <Alert severity="error" sx={{mt: 2}}>{message}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button type="button" disabled={busy} onClick={close}>Close</Button>
          <Button type="submit" variant="contained" disabled={busy || !password || (action === 'cancel' ? !account.pendingEmail : !email.trim())}>
            {busy ? 'Please wait…' : action === 'cancel' ? 'Confirm cancellation' : 'Send confirmation'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  </Box>;
}
